import React, { useState, useEffect, useRef } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Checkbox,
	Button,
	IconButton,
	Box,
	Alert,
	TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const DAY_NAMES = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];

export function GridContract({ onDataUpdate, showError }) {
	const { t } = useTranslation();
	const initializedRef = useRef(false);
	const [grid, setGrid] = useState({});

	// 1. Inicializar grid vacío solo una vez
	useEffect(() => {
		if (initializedRef.current) return;
		const initial = {};
		DAY_NAMES.forEach((day) => {
			initial[day] = {
				config_contracts_per_days_id: null,
				config_contracts_per_periods_id: null,
				selected: false,
				periods: [],
			};
		});
		setGrid(initial);
		initializedRef.current = true;
		// Emitir estado vacío inicial
		onDataUpdate(
			DAY_NAMES.map((day, idx) => ({ config_contracts_per_days_id: null, day: idx, value: 0 })),
			[],
			true
		);
	}, [onDataUpdate]);

	// 2. Emit update para Create: days, periods y allEmpty
	const emitUpdate = (updatedGrid) => {
		const daysArr = DAY_NAMES.map((day, idx) => ({
			config_contracts_per_days_id: updatedGrid[day].config_contracts_per_days_id,
			day: idx,
			value: updatedGrid[day].selected ? 1 : 0,
		}));

		const periodsArr = DAY_NAMES.map((day) => ({
			config_contracts_per_days_id: updatedGrid[day].config_contracts_per_days_id,
			config_contracts_per_periods_id: updatedGrid[day].config_contracts_per_periods_id,
			// si no hay periodos, arranca como [] y así el backend recibe 7 grupos
			periods: updatedGrid[day].periods.map((p) => ({
				start_time: p.start,
				end_time: p.end,
			})),
		}));

		const allEmpty = DAY_NAMES.every((day) => !updatedGrid[day].selected);
		onDataUpdate(daysArr, periodsArr, allEmpty);
	};

	// 3. Handlers
	const toggleDay = (day) =>
		setGrid((g) => {
			const sel = !g[day].selected;
			const periods = sel ? [{ start: "", end: "", error: "" }] : [];
			const next = { ...g, [day]: { ...g[day], selected: sel, periods } };
			emitUpdate(next);
			return next;
		});

	const addPeriod = (day) =>
		setGrid((g) => {
			const next = {
				...g,
				[day]: {
					...g[day],
					periods: [...g[day].periods, { start: "", end: "", error: "" }],
				},
			};
			emitUpdate(next);
			return next;
		});

	const removePeriod = (day, idx) =>
		setGrid((g) => {
			const nextPeriods = g[day].periods.filter((_, i) => i !== idx);
			const next = { ...g, [day]: { ...g[day], periods: nextPeriods } };
			emitUpdate(next);
			return next;
		});

	const onTimeChange = (day, idx, field, val) => {
		const formatted = val ? dayjs(val).format("HH:mm:ss") : "";
		setGrid((g) => {
			const nextPeriods = g[day].periods.map((p, i) => (i === idx ? { ...p, [field]: formatted } : p));
			// Validación simple
			let err = "";
			const { start, end } = nextPeriods[idx];
			if (start && end && dayjs(end).isBefore(dayjs(start))) {
				err = t("aux.grid_d_p.error_format");
			}
			nextPeriods[idx].error = err;
			const next = { ...g, [day]: { ...g[day], periods: nextPeriods } };
			emitUpdate(next);
			return next;
		});
	};

	// 4. UI flags
	const allEmpty = DAY_NAMES.every((day) => !grid[day]?.selected);
	const hasEmptyPair = DAY_NAMES.some(
		(day) => grid[day]?.selected && grid[day].periods.some((p) => !p.start || !p.end)
	);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<TableContainer sx={{ width: "100%", overflowX: "hidden" }}>
				<Table size="small" sx={{ minWidth: 0 }}>
					<TableHead>
						<TableRow>
							<TableCell>{t("aux.day")}</TableCell>
							<TableCell>{t("aux.attend")}</TableCell>
							<TableCell>{t("aux.period")}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{DAY_NAMES.map((day) => (
							<TableRow key={day}>
								<TableCell>{t(`aux.days.${day}`)}</TableCell>
								<TableCell>
									<Checkbox checked={grid[day]?.selected || false} onChange={() => toggleDay(day)} />
								</TableCell>
								<TableCell>
									{grid[day]?.selected &&
										grid[day].periods.map((p, i) => (
											<Box key={i} sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
												<TimePicker
													label={t("aux.start_time")}
													value={p.start ? dayjs(p.start, "HH:mm:ss") : null}
													onChange={(v) => onTimeChange(day, i, "start", v)}
													ampm={false}
													views={["hours", "minutes"]}
													inputFormat="HH:mm"
													mask="__:__"
													minutesStep={60}
													enableAccessibleFieldDOMStructure={false}
													slots={{ textField: TextField }}
													slotProps={{
														textField: {
															size: "small",
															sx: {
																m: 1,
																width: "100px",
																minWidth: "60px",
															},
															InputProps: {
																sx: {
																	padding: "3px",
																	"& .MuiInputBase-input": {
																		fontSize: "1rem",
																		padding: "3px 5px",
																	},
																},
															},
															inputProps: {
																style: {
																	fontSize: "1rem",
																	padding: "6px 2px",
																},
															},
														},
													}}
												/>
												<TimePicker
													label={t("aux.end_time")}
													value={p.end ? dayjs(p.end, "HH:mm:ss") : null}
													onChange={(v) => onTimeChange(day, i, "end", v)}
													ampm={false}
													views={["hours", "minutes"]}
													inputFormat="HH:mm"
													mask="__:__"
													minutesStep={60}
													enableAccessibleFieldDOMStructure={false}
													slots={{ textField: TextField }}
													slotProps={{
														textField: {
															size: "small",
															sx: {
																m: 1,
																width: "100px",
																minWidth: "60px",
															},
															InputProps: {
																sx: {
																	padding: "3px",
																	"& .MuiInputBase-input": {
																		fontSize: "1rem",
																		padding: "3px 5px",
																	},
																},
															},
															inputProps: {
																style: {
																	fontSize: "1rem",
																	padding: "6px 2px",
																},
															},
														},
													}}
												/>
												<IconButton size="small" onClick={() => removePeriod(day, i)}>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Box>
										))}
									{grid[day]?.selected && (
										<Button size="small" onClick={() => addPeriod(day)}>
											{t("aux.buttons.more")}
										</Button>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			{showError && allEmpty && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{t("aux.messages.alert.grid_d_p.error")}
				</Alert>
			)}
			{!allEmpty && hasEmptyPair && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{t("aux.messages.alert.grid_d_p.pairs_error")}
				</Alert>
			)}
		</LocalizationProvider>
	);
}
